import * as React from "react";
import { Box, FormControlLabel, Grid, Paper, Radio, RadioGroup, Switch, Typography, Stack, useTheme } from "@mui/material";

export type AssistanceItemCard = {
	key: string;
	label: string;
	enabled: boolean;
	isDefault: boolean;
};

type Props = {
	isLoading: boolean;
	assistances: AssistanceItemCard[];
	setAssistances: (next: AssistanceItemCard[]) => void;
};

export const AssistanceCard: React.FC<Props> = ({ isLoading, assistances, setAssistances }) => {
	const theme = useTheme();
	const defaultKey = React.useMemo(() => assistances.find((a) => a.isDefault)?.key || "", [assistances]);

	const handleToggleEnabled = (idx: number, checked: boolean) => {
		const next = assistances.map((a, i) => (i === idx ? { ...a, enabled: checked, isDefault: checked ? a.isDefault : false } : a));
		setAssistances(next);
	};

	const handleChooseDefault = (key: string) => {
		const next = assistances.map((a) => (a.key === key ? { ...a, isDefault: true, enabled: true } : { ...a, isDefault: false }));
		setAssistances(next);
	};

	return (
		<Box margin={1} component={Paper} variant="elevation" elevation={0}>
			<Box px={2} pb={2}>
				<RadioGroup name="assistances_default_selector" value={defaultKey} onChange={(e) => handleChooseDefault(e.target.value)}>
					<Grid container spacing={1.5}>
						{assistances.map((item, index) => {
							const isDefault = item.isDefault;
							const isEnabled = item.enabled;

							return (
								<Grid item xs={12} sm={6} md={4} lg={3} key={`assistance-${item.key || index}`}>
									{/* hidden inputs p/ VForm (mantidos) */}
									<input type="hidden" name={`assistances[${index}].key`} value={item.key} />
									<input type="hidden" name={`assistances[${index}].label`} value={item.label} />
									<input type="hidden" name={`assistances[${index}].enabled`} value={String(item.enabled)} />
									<input type="hidden" name={`assistances[${index}].isDefault`} value={String(item.isDefault)} />

									<Box
										sx={{
											// Borda: Usa divider do tema para cor neutra (ajusta-se ao dark mode)
											border: `1px solid ${isDefault ? theme.palette.primary.main : theme.palette.divider}`,
											borderRadius: 2,
											p: 1.5,
											minHeight: 110,
											bgcolor: theme.palette.background.paper,
											boxShadow: isDefault ? theme.shadows[4] : theme.shadows[1],
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											transition: "all .2s ease",
											"&:hover": {
												boxShadow: isDefault ? theme.shadows[8] : theme.shadows[3],
											},
										}}
									>
										<Stack spacing={1} alignItems="center" justifyContent="center" sx={{ width: "100%" }}>
											<Typography
												variant="subtitle1"
												sx={{
													textAlign: "center",
													fontWeight: 600,
													letterSpacing: 0.3,
												}}
											>
												{item.label}
											</Typography>

											<FormControlLabel
												sx={{ m: 0 }}
												control={
													<Switch
														size="small"
														color="secondary"
														checked={isEnabled}
														onChange={(e) => handleToggleEnabled(index, e.target.checked)}
														disabled={isLoading}
													/>
												}
												label="Ativo"
											/>

											{/* O Radio para 'Padrão' só pode ser escolhido se estiver Ativo */}
											<FormControlLabel
												sx={{ m: 0 }}
												value={item.key || String(index)}
												control={<Radio size="small" color="primary" disabled={isLoading || !isEnabled} />}
												label="Padrão"
											/>
										</Stack>
									</Box>
								</Grid>
							);
						})}

						{assistances.length === 0 && (
							<Grid item xs={12}>
								<Typography variant="body2" color="text.secondary">
									Nenhuma assistência disponível.
								</Typography>
							</Grid>
						)}
					</Grid>
				</RadioGroup>
			</Box>
		</Box>
	);
};
